"use client";

import Link from "next/link";
import { Building2, Mail, MapPin } from "lucide-react";

import { LEGAL_COMPANY, LEGAL_DOC_LINKS, formatCompanyAddress } from "@/content/company";
import { getLegalContent } from "@/content/legal";
import { getLegalPolicyTitle } from "@/content/legal-policies";
import { useLocale } from "@/lib/i18n/provider";
import { localizePath } from "@/lib/i18n/routing";

export default function CompanyIdentityBlock({ compact = false }: { compact?: boolean }) {
  const { locale } = useLocale();
  const legal = getLegalContent(locale);
  const identity = legal.hub.companyIdentity;

  if (compact) {
    return (
      <div className="flex flex-col gap-1 text-xs text-[var(--color-text-muted)]">
        <span className="font-medium text-[var(--color-text-secondary)]">{LEGAL_COMPANY.legalName}</span>
        <span>
          {identity.cifLabel}: {LEGAL_COMPANY.cif} · {identity.vatLabel}: {LEGAL_COMPANY.vat}
        </span>
        <a
          href={`mailto:${LEGAL_COMPANY.supportEmail}`}
          className="transition-colors hover:text-[var(--color-text-primary)]"
        >
          {LEGAL_COMPANY.supportEmail}
        </a>
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-surface-raised)] p-6 md:p-8">
      <div className="flex items-start gap-3">
        <Building2 size={18} className="mt-0.5 text-[var(--color-accent-base)]" aria-hidden="true" />
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{identity.title}</h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{identity.subtitle}</p>
        </div>
      </div>

      <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {identity.legalNameLabel}
          </dt>
          <dd className="mt-1 text-[var(--color-text-primary)]">{LEGAL_COMPANY.legalName}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {identity.cifLabel} / {identity.vatLabel}
          </dt>
          <dd className="mt-1 text-[var(--color-text-primary)]">
            {LEGAL_COMPANY.cif} · {LEGAL_COMPANY.vat}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            <MapPin size={12} aria-hidden="true" />
            {identity.addressLabel}
          </dt>
          <dd className="mt-1 text-[var(--color-text-secondary)]">{formatCompanyAddress(locale)}</dd>
        </div>
        <div>
          <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            <Mail size={12} aria-hidden="true" />
            {identity.supportLabel}
          </dt>
          <dd className="mt-1">
            <a
              href={`mailto:${LEGAL_COMPANY.supportEmail}`}
              className="text-[var(--color-text-primary)] transition-colors hover:text-[var(--color-accent-base)]"
            >
              {LEGAL_COMPANY.supportEmail}
            </a>
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {identity.privacyLabel}
          </dt>
          <dd className="mt-1">
            <a
              href={`mailto:${LEGAL_COMPANY.email}`}
              className="text-[var(--color-text-primary)] transition-colors hover:text-[var(--color-accent-base)]"
            >
              {LEGAL_COMPANY.email}
            </a>
          </dd>
        </div>
      </dl>
    </section>
  );
}

export function LegalDocLinksList() {
  const { locale } = useLocale();
  const legal = getLegalContent(locale);

  // Prefer one link per page (skip duplicate impressum slug if legal-notice present).
  const seen = new Set<string>();
  const links = LEGAL_DOC_LINKS.filter((doc) => {
    if (seen.has(doc.page) && doc.page === "impressum") return false;
    seen.add(doc.page);
    return true;
  });

  return (
    <ul className="mt-4 flex flex-col gap-3">
      {links.map((doc) => {
        let title: string;
        if (doc.titleKey === "legalNotice") {
          title = legal.legalNotice.title;
        } else if (doc.titleKey) {
          title = legal[doc.titleKey].title;
        } else {
          try {
            title = getLegalPolicyTitle(doc.page, locale);
          } catch {
            title = doc.page.replace(/_/g, " ");
          }
        }

        return (
          <li key={doc.href}>
            <Link
              href={localizePath(doc.href, locale)}
              className="group flex items-center justify-between rounded-xl border border-[var(--color-surface-border)] px-4 py-3 text-sm transition-colors hover:border-[var(--color-accent-base)] hover:bg-[var(--color-surface-raised)]"
            >
              <span className="font-medium text-[var(--color-text-primary)]">{title}</span>
              <span className="text-[var(--color-text-muted)] transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
